/**
 * Boxplot chart builder (ADR-0079 §4.1, §4.4).
 *
 * Pre-computed 5-number summary per row (min / q1 / median / q3 / max).
 * The renderer maps each row to ECharts's `[min, q1, median, q3, max]`
 * series shape — that's the order ECharts requires.
 *
 * Raw-values mode (renderer computes the summary from raw observations) is a
 * v2 add per ADR-0079 §10 OQ.
 */

import type { BoxplotSpec } from "../echartsSpec.js";
import {
  buildAxisLabelFormatter,
  buildNoDataGraphic,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

export function buildBoxplotOption(
  spec: BoxplotSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const xIdx = resolveColumnIndex(spec.encoding.x.column, dimensions);
  const minIdx = resolveColumnIndex(spec.encoding.summary.min, dimensions);
  const q1Idx = resolveColumnIndex(spec.encoding.summary.q1, dimensions);
  const medianIdx = resolveColumnIndex(spec.encoding.summary.median, dimensions);
  const q3Idx = resolveColumnIndex(spec.encoding.summary.q3, dimensions);
  const maxIdx = resolveColumnIndex(spec.encoding.summary.max, dimensions);

  const allResolved =
    xIdx !== null && minIdx !== null && q1Idx !== null && medianIdx !== null && q3Idx !== null && maxIdx !== null;

  const categories: unknown[] = [];
  // Data = [[min, q1, median, q3, max], ...] (ECharts convention)
  const data: Array<[number | null, number | null, number | null, number | null, number | null]> = [];

  if (allResolved) {
    for (const row of rows) {
      categories.push(row[xIdx as number]);
      data.push([
        coerce(row[minIdx as number]),
        coerce(row[q1Idx as number]),
        coerce(row[medianIdx as number]),
        coerce(row[q3Idx as number]),
        coerce(row[maxIdx as number]),
      ]);
    }
  }

  const xAxis: Record<string, unknown> = {
    type: spec.encoding.x.dataType,
    data: spec.encoding.x.dataType === "category" ? categories : undefined,
  };

  const yAxis: Record<string, unknown> = {
    type: "value",
    scale: true,
    ...(spec.options.yAxis?.min !== undefined ? { min: spec.options.yAxis.min } : {}),
    ...(spec.options.yAxis?.max !== undefined ? { max: spec.options.yAxis.max } : {}),
    ...(spec.options.yAxis?.format
      ? { axisLabel: { formatter: buildAxisLabelFormatter(spec.options.yAxis.format) } }
      : {}),
  };

  const palette = resolvePalette(spec.options.color, 1);

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: { show: spec.options.legend?.show !== false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
    },
    xAxis,
    yAxis,
    series: [
      {
        type: "boxplot",
        data,
      },
    ],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
